import React, { useState, useRef, useEffect } from 'react';
import { useTransform, useViewportScroll, useElementScroll } from 'framer-motion';
import {
  LeftColumn,
  RightColumn,
  Wrapper,
  BackgroundOverlay,
  MainTitle,
  AndSign,
  CtaContainer,
} from './StickyCardsStack.styles';
import StickyCardsStackItem from '../StickyCardsStackItem/StickyCardsStackItem';
import useDeviceDetection from '../../hooks/useDeviceDetection';
import Image from '../Image';
import ButtonLink from '../interface/ButtonLink';

const StickyCardsStack = ({ fixedImage, items, title, cta1, cta2, linkTitle, linkTitle2 }) => {
  const CARD_GRID_GAP = 50;

  const ref = useRef();
  const cardsWrapperRef = useRef();
  const [cardsAreStacked, setCardsAreStacked] = useState(false);
  const [firstCardDistanceFromTop, setFirstCardDistanceFromTop] = useState(0);
  const { scrollY } = useViewportScroll();
  const { scrollY: elementScrollY } = useElementScroll(ref);
  const { isDesktop } = useDeviceDetection();

  // wrapper transforms
  const y = useTransform(scrollY, [0, 1000], [0, -100]);
  const opacity = useTransform(scrollY, [0, 1000], [0, 0.5]);

  // add a ghost card to make sure the 'last' item is not scrolling beyond the other items
  const stickyItems = [...items, { id: 'ghost' }];

  useEffect(() => {
    if (!isDesktop) return;
    // state
    let cardsAreStacked = false;

    // vars
    const { children } = cardsWrapperRef.current;
    const firstCard = children[0];
    const lastCard = children[children.length - 2]; // minus 2 because the last card is a ghost card
    const halfWayTop = window.innerHeight / 2 - firstCard.getBoundingClientRect().height / 2;
    const content = document.querySelector('.content');

    // calculate at which point the last card should stop scrolling
    const lastCardDistanceFromTop = halfWayTop + CARD_GRID_GAP * (children.length - 1);
    setFirstCardDistanceFromTop(halfWayTop);

    const handleScroll = e => {
      const lastCardFromTop = lastCard.getBoundingClientRect().top;
      const contentFromTop = content.getBoundingClientRect().top;
      const isScrollingDown = Math.sign(e.deltaY) > 0;

      if (contentFromTop >= window.innerHeight) {
        cardsAreStacked = false;
        setCardsAreStacked(false);
      } else {
        cardsAreStacked = true;
        setCardsAreStacked(true);
      }

      if (lastCardFromTop <= lastCardDistanceFromTop && isScrollingDown) {
        ref.current.style.pointerEvents = 'none';
      } else if (!cardsAreStacked && !isScrollingDown) {
        ref.current.style.pointerEvents = 'all';
      }
    };

    window.addEventListener('wheel', handleScroll);
    return () => window.removeEventListener('wheel', handleScroll);
  }, [ref, isDesktop]);

  return (
    <>
      <Wrapper ref={ref} style={cardsAreStacked && isDesktop && { y }}>
        <LeftColumn>
          {fixedImage?.[0] && <Image image={fixedImage[0]} lazyload={false} />}
          <MainTitle>{title}</MainTitle>
          <AndSign>&</AndSign>
          {cta1 && cta2 && linkTitle && linkTitle2 && (
            <CtaContainer>
              <ButtonLink href={cta1.url} size="large" className="normal-btn">
                {linkTitle}
              </ButtonLink>
              <ButtonLink href={cta2.url} size="large" className="reversed-btn" color="black">
                {linkTitle2}
              </ButtonLink>
            </CtaContainer>
          )}
        </LeftColumn>
        <RightColumn ref={cardsWrapperRef} firstCardDistanceFromTop={firstCardDistanceFromTop}>
          {stickyItems?.map((item, index) => {
            const distanceWhenSticky = Math.floor(firstCardDistanceFromTop + index * CARD_GRID_GAP);

            return (
              <StickyCardsStackItem
                key={item.id}
                item={item}
                index={index}
                elementScrollY={elementScrollY}
                distanceWhenSticky={distanceWhenSticky}
              />
            );
          })}
        </RightColumn>
      </Wrapper>
      <BackgroundOverlay style={cardsAreStacked && isDesktop && { opacity }} />
    </>
  );
};

export default StickyCardsStack;
